// InvoiceDetail — /office/invoices/new and /office/invoices/:id.
//
// The form is DocumentEditor; this screen owns the requests: GET /api/invoices/:id,
// POST /api/invoices, PUT /api/invoices/:id, POST /:id/send, PUT /:id/mark-paid,
// PUT /:id/mark-unpaid, PUT /:id/paid-date,
// POST /:id/sync-payment, DELETE /:id, the public link and the photo ZIP.
// Totals always come back from the server after a save — the editor never
// computes one.
//
// "Download PDF" is the server's PDF (GET /:id/pdf, server/utils/invoicePdf.js)
// — the same file the invoice email attaches and the client downloads from
// their link. "Print" is still the PRINT VIEW: the document is on the page
// rather than hidden behind a media query, so what will print can be read at
// 390px before anyone commits paper to it.
import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FwButton, Notice, Pill, SectionLabel, EmptyState, FwField, FwInput } from '../../../ui/fw';
import { useSettings } from '../../../App';
import { useRole } from '../../../hooks/usePermission';
import { get, post, put, del, describeError } from '../../shared/http';
// The upload budget, declared once for every screen that sends photos, and
// checked against server/routes/media.js and deploy.sh by photoBatches.test.js.
import { uploadInBatches, uploadSummary } from '../../shared/photoBatches';
import { fmtMoney, fmtDate, fmtDateTime, todayIso } from '../shared/format';
import { invoicePill } from '../shared/status';
import { downloadBlob, copyText } from '../shared/download';
import { PageHeader, Refused, LoadFailed, Loading, ActionError, ConfirmSheet, PickerFailed } from '../shared/Page';
import { DocumentEditor, DocumentSummary } from '../shared/DocumentEditor';
import { ProvenanceCard } from '../shared/provenance';
import { InvoicePrintDocument } from './InvoicePrint';
import { mayUse, pathFor, mismatchSentence, reversalOutcome, sendRecipient } from '../shared/moneyEndpoints';
import { PaidDayField, companyToday, companyZone, paidDayBody, paidDayOf, paidDayProblem } from '../shared/paidDay';

/**
 * Where a cancelled invoice goes back to. Owner, 2026-09-29, "do what makes
 * the most sense": three notices already told the office to "reinstate the
 * invoice and mark it paid" (cancelOutcome below, routes/invoices.js
 * closeStripeFor, utils/stripeWebhook.js) and no screen could.
 *
 * One that was sent goes back to Sent, so its due date and its client link
 * mean what they meant; one never sent goes back to Draft. PUT /invoices/:id
 * stamps sent_at only on a first send, so Sent keeps the day it really went.
 * Nothing is recalculated: the status is all that is sent.
 */
export function reinstateTo(doc) {
  return doc && doc.sent_at ? 'sent' : 'draft';
}

/**
 * What to tell the office once an invoice is cancelled, from the `stripe`
 * block PUT /invoices/:id answers with (routes/invoices.js closeStripeFor).
 * No block means Stripe is not set up and nothing was ever opened there.
 *
 * The two Stripe steps fail independently, so a failure says WHICH is still
 * open (review of #732). `failed` tells the screen to add the way to retry;
 * the sentence never names a button itself, because which one exists depends
 * on the invoice.
 */
export function cancelOutcome(r) {
  const s = r && r.stripe;
  if (s && (s.voidError || s.checkoutError || s.error)) {
    const open = [];
    if (s.voidError || (!s.checkoutError && s.error)) open.push(`its Stripe invoice could not be voided (${s.voidError || s.error})`);
    if (s.checkoutError) {
      open.push(`an open online checkout could not be closed (${s.checkoutError}); one closes by itself within 24 hours of being opened`);
    }
    const lead = s.voided && !s.voidError ? 'Cancelled, and its Stripe invoice was voided, but ' : 'Cancelled here, but ';
    return { tone: 'warn', failed: true, text: `${lead}${open.join(', and ')}. It may still be payable online.` };
  }
  if (s && s.paid) {
    return { tone: 'warn', text: 'Cancelled. Stripe shows this invoice as already paid, so nothing was voided: refund the payment in Stripe, or reinstate the invoice and mark it paid.' };
  }
  if (s && (s.voided || s.deleted)) return { tone: 'ok', text: `Cancelled. Its Stripe invoice was ${s.voided ? 'voided' : 'deleted'}, so the client can no longer pay it.` };
  return { tone: 'ok', text: 'Cancelled. The client can no longer pay it.' };
}

/**
 * What to tell the office once an invoice is settled here — Mark paid, or a
 * recorded payment that cleared the balance — from the same `stripe` block
 * (closeStripeFor). The mark itself always stands; only Stripe's half can
 * need the office (review of #739: the screen said a flat "Marked paid."
 * while the response carried a refusal). Null when there is nothing to add.
 */
export function settledOutcome(r) {
  const s = r && r.stripe;
  if (!s) return null;
  if (s.voidError || s.checkoutError || s.error) {
    const open = [];
    if (s.voidError || (!s.checkoutError && s.error)) open.push(`its Stripe invoice could not be voided (${s.voidError || s.error})`);
    if (s.checkoutError) open.push(`an open online checkout could not be closed (${s.checkoutError}); one closes by itself within 24 hours of being opened`);
    return { tone: 'warn', text: `Paid here, but ${open.join(', and ')}. It may still be payable online: close it in the Stripe dashboard.` };
  }
  if (s.paid) {
    return { tone: 'warn', text: 'Paid here, and Stripe shows this invoice as paid online too. If the client also paid another way, refund one of the two in the Stripe dashboard.' };
  }
  if (s.voided || s.deleted) return { tone: 'ok', text: `Its Stripe invoice was ${s.voided ? 'voided' : 'deleted'}, so the client can no longer pay it online.` };
  if (Array.isArray(s.expired) && s.expired.length) return { tone: 'ok', text: 'Its open online checkout was closed, so the client can no longer pay it online.' };
  return null;
}

export default function InvoiceDetail() {
  const { id } = useParams();
  const isNew = !id;
  const role = useRole();
  // Every gate here is the ROLE. invoices.js is checkRole(...) end to end — a
  // role list with no extra_permissions escape — so useCan would give this
  // screen a second opinion that disagrees with the server in BOTH directions:
  // it refused managers the server serves, and admitted accounting it refuses
  // (moneyEndpoints.js header, roleGateDivergence.test.js).
  const canRead = mayUse('invoiceRead', role);
  const canCreate = mayUse('invoiceCreate', role);
  const canUpdate = mayUse('invoiceUpdate', role);
  const canSync = mayUse('syncOnePayment', role);
  // Each ACTION is gated on the endpoint IT calls, not on a neighbouring one.
  //
  // Send and Mark paid were both gated on `canUpdate` — that is, on PUT /:id —
  // while calling POST /:id/send and PUT /:id/mark-paid. It read as correct
  // for a year because all three admitted the same two roles, so the mistake
  // was invisible until one of them moved: on 2026-09-06 mark-paid was widened
  // to the bookkeeping role and Mark paid still did not appear, because the
  // button was asking about a different door. Found in a browser, not by a
  // test — every unit test was green.
  //
  // Delete was the same shape and right by accident: a bare `role === 'admin'`
  // that happens to equal DELETE /:id's gate. It goes through the table now, so
  // that when the table moves the button moves with it.
  const canSend = mayUse('invoiceSend', role);
  const canMarkPaid = mayUse('markInvoicePaid', role);
  const canDelete = mayUse('invoiceDelete', role);
  const canReverse = mayUse('markInvoiceUnpaid', role);
  const canRedate = mayUse('changeInvoicePaidDate', role);
  const canRecord = mayUse('recordInvoicePayment', role);
  const { settings } = useSettings();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const inv = useQuery({ queryKey: ['office', 'invoice', id], queryFn: () => get(`/invoices/${id}`), enabled: canRead && !isNew, staleTime: Infinity });
  const clients = useQuery({ queryKey: ['office', 'clients'], queryFn: () => get('/clients?active_only=true'), enabled: canRead });
  const jobs = useQuery({ queryKey: ['office', 'invoices', 'available-jobs'], queryFn: () => get('/invoices/available-jobs'), enabled: canRead && (isNew ? canCreate : canUpdate) });

  const [saving, setSaving] = useState(false);
  // Bumped only when a save from the FORM landed. DocumentEditor reloads
  // itself from the server on this and on the document's id — never merely
  // because react-query handed back a new object, which is how attaching a
  // photo used to throw away unsaved line edits (DocumentEditor's reset
  // effect carries the browser measurement).
  const [savedAt, setSavedAt] = useState(0);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reinstateOpen, setReinstateOpen] = useState(false);
  // What Stripe did when the invoice was cancelled — its own note, like the
  // sync answer, because "could not be told" must never read as a green done.
  const [cancelNote, setCancelNote] = useState(null);
  const [reverseOpen, setReverseOpen] = useState(false);
  const [reverseReason, setReverseReason] = useState('');
  const [reverseCollected, setReverseCollected] = useState('');
  // The day it was paid. Asked on Mark paid, and changeable once paid, because
  // the day decides which year's report the money is in (shared/paidDay.js).
  const [paidOpen, setPaidOpen] = useState(false);
  const [paidDay, setPaidDay] = useState('');
  // Record payment: part or all of what is owed (classic Billing, restored
  // 2026-10-09). The refusal is shown INSIDE the sheet — the page's error bar
  // is behind it (field/StopSheet.js learned that first).
  const [recordOpen, setRecordOpen] = useState(false);
  const [recordAmount, setRecordAmount] = useState('');
  const [recordDay, setRecordDay] = useState('');
  const [recordNote, setRecordNote] = useState('');
  const [recordError, setRecordError] = useState(null);
  // Stripe's half of settling it, when the office has something to do about
  // it (settledOutcome). Stays until dismissed, like cancelNote.
  const [stripeNote, setStripeNote] = useState(null);
  const [redateOpen, setRedateOpen] = useState(false);
  const [redateDay, setRedateDay] = useState('');
  const [redateReason, setRedateReason] = useState('');
  const [busy, setBusy] = useState(null);
  // The sync answer is NOT a flash: `updated: false` means nothing changed, and
  // showing it in the green bar the save uses would read as a success.
  const [syncNote, setSyncNote] = useState(null);
  const [printing, setPrinting] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  // Not a flash and not an ActionError: a partial upload is neither a success
  // nor a request that failed. It is a COUNT of what landed and what did not,
  // worded by apps/shared/photoBatches.js so this screen and the Field one
  // cannot describe the same failure differently.
  const [photoNote, setPhotoNote] = useState(null);
  const [photoProgress, setPhotoProgress] = useState(null);
  const [dropPhoto, setDropPhoto] = useState(null);

  const doc = isNew ? null : inv.data;
  const today = todayIso();
  const clientRecord = useMemo(() => {
    if (!doc || !doc.client_id || !clients.data) return null;
    return clients.data.find((c) => Number(c.id) === Number(doc.client_id)) || null;
  }, [doc, clients.data]);
  // Where a send would actually GO, decided by the rule the route applies
  // (shared/moneyEndpoints.js sendRecipient → server/utils/invoiceContact.js).
  // `clientRecord` is null both when the client genuinely has no row and when
  // the roster this screen holds simply does not contain it — the query is
  // active_only — which is why the answer carries `known` and the sheet below
  // only refuses on `known`.
  const to = useMemo(() => sendRecipient(doc, clientRecord), [doc, clientRecord]);
  const recipient = to.email || '';

  if (!canRead) return <Refused what="invoices" />;
  if (isNew && !canCreate) return <Refused what="creating invoices" />;
  if (!isNew && inv.isPending) return <><PageHeader title="Invoice" /><Loading lines={8} /></>;
  if (!isNew && inv.isError) return <><PageHeader title="Invoice" /><LoadFailed error={inv.error} onRetry={() => inv.refetch()} what="this invoice" /></>;

  // The print view replaces the page rather than hiding behind @media print,
  // so what will come out of the printer is legible on screen first — at 390px
  // as well as 1440px. The bar is the only part `@media print` removes.
  if (printing && doc) {
    return (
      <div data-screen="office-InvoicePrint">
        <div className="of-print__bar">
          <FwButton variant="primary" icon="document" onClick={() => window.print()} data-action="print">Print / Save PDF</FwButton>
          <FwButton variant="ghost" icon="arrowLeft" onClick={() => setPrinting(false)} data-action="print-back">Back to the invoice</FwButton>
        </div>
        <InvoicePrintDocument doc={doc} settings={settings || {}} />
      </div>
    );
  }

  const paid = doc && doc.status === 'paid';
  const cancelled = doc && doc.status === 'cancelled';
  const canEdit = isNew ? canCreate : (canUpdate && !paid);
  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['office', 'invoice', id] });
    await qc.invalidateQueries({ queryKey: ['office', 'invoices'] });
  };

  const save = async (payload) => {
    setSaving(true); setError(null); setFlash(null);
    try {
      if (isNew) {
        const created = await post('/invoices', payload);
        await qc.invalidateQueries({ queryKey: ['office', 'invoices'] });
        navigate(`/invoices/${created.id}`, { replace: true });
      } else {
        await put(`/invoices/${id}`, payload);
        await refresh();
        setSavedAt((n) => n + 1);
        setFlash('Saved. The totals below are what the server calculated.');
      }
    } catch (e) { setError(e); } finally { setSaving(false); }
  };

  /**
   * Attaching photos to an invoice.
   *
   * Two requests, because the server splits the work in two:
   *
   *   POST /media/upload        multipart, field name `files`, max 10 x 25MB.
   *                             Stores the bytes and returns file_path for
   *                             each. It attaches them to NOTHING — it is
   *                             deliberately not job-scoped (media.js:603).
   *   PUT  /invoices/:id        `photos` is persisted to invoice_photos with
   *                             invoice_item_id NULL (invoices.js:569).
   *
   * The PUT REPLACES the invoice-level gallery, so every write sends the whole
   * list — existing rows included, captions and media_id preserved. Sending
   * only the new ones would delete the old ones, and a before-photo deleted
   * off an invoice is not recoverable from the app.
   *
   * Nothing else is sent. Totals are recomputed only when `items` is non-empty
   * or when discount/tax is present (invoices.js:544-556), so a photos-only
   * PUT cannot move a number on a bill. That is the whole reason this is safe
   * to do from here.
   */
  const writePhotos = async (next) => {
    await put(`/invoices/${id}`, {
      photos: next.map((p) => ({
        media_id: p.media_id || null,
        file_path: p.file_path,
        caption: p.caption || '',
      })),
    });
    await refresh();
  };

  /**
   * A selection of any size, sent as however many requests it takes.
   *
   * This built ONE FormData out of whatever the picker returned and posted it.
   * The binding limit was never the file count — it is nginx's
   * `client_max_body_size 50M` (deploy.sh:670, :747), which is enforced before
   * Express sees a byte and which no server-side count can clear at 25MB a
   * file. So a manager attaching a set of before-and-after shots got one
   * refusal with nothing stored, exactly as a crew member did (#656).
   * `../../shared/photoBatches.js` holds the three numbers and the arithmetic.
   *
   * What is different HERE, and why this was not the three-line swap the Field
   * path was: storing the bytes and ATTACHING them are two separate requests,
   * and the second one writes to a money document.
   *
   *   · `uploadInBatches` never throws. It returns what happened, so the
   *     failure has to be read off `out`, not caught — a try/catch around it
   *     reports a partial upload as a success, which is the one outcome this
   *     whole change exists to prevent.
   *   · Whatever landed is LINKED, even when a later batch was refused. Those
   *     bytes are on the server's disk; an invoice that references none of them
   *     is an upload the office paid for, a client's copy showing nothing, and
   *     a manager told to pick all thirty again.
   *   · ONE PUT, after every batch, not one per batch. The PUT REPLACES the
   *     invoice-level gallery, and five of them each built from a `doc` that is
   *     one refetch behind is how four batches' photos get deleted by the fifth.
   *   · The body is still `{ photos }` and nothing else, whether it took one
   *     request or five — which is what keeps invoices.js:544-556 from
   *     recomputing a total (CLAUDE.md rule 4).
   */
  const addPhotos = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setPhotoBusy(true); setError(null); setFlash(null); setPhotoNote(null);
    setPhotoProgress({ sent: 0, total: files.length });
    try {
      const out = await uploadInBatches(files, (batch) => {
        const fd = new FormData();
        batch.forEach((f) => fd.append('files', f, f.name));
        return post('/media/upload', fd);
      }, { onProgress: setPhotoProgress });

      const added = (out.results || []).flatMap((up) => ((up && up.files) || []).map((f) => ({
        media_id: null, file_path: f.file_path, caption: '',
      })));
      // Said BEFORE the link is attempted, so a refused PUT cannot swallow the
      // count of what did upload.
      const trouble = uploadSummary(out);
      if (trouble) setPhotoNote(trouble);

      if (added.length) await writePhotos([...((doc && doc.photos) || []), ...added]);
      else await refresh();

      if (!trouble) setFlash(added.length === 1 ? 'Photo attached.' : `${added.length} photos attached.`);
    } catch (e) {
      // Only the PUT reaches here. The bytes are stored and this invoice
      // references none of them, so the gallery is refetched anyway rather than
      // left showing a state nobody confirmed.
      setError(e);
      try { await refresh(); } catch (_) { /* the error above is the one to read */ }
    } finally { setPhotoBusy(false); setPhotoProgress(null); }
  };

  const removePhoto = async () => {
    const target = dropPhoto;
    if (!target) return;
    setPhotoBusy(true); setError(null); setFlash(null);
    try {
      await writePhotos(((doc && doc.photos) || []).filter((p) => p !== target));
      setDropPhoto(null);
      setFlash('Photo removed from this invoice.');
    } catch (e) { setError(e); } finally { setPhotoBusy(false); }
  };

  const run = async (name, fn) => {
    setBusy(name); setError(null); setFlash(null);
    try { await fn(); } catch (e) { setError(e); } finally { setBusy(null); }
  };
  const send = () => run('send', async () => {
    const r = await post(`/invoices/${id}/send`);
    setSendOpen(false);
    await refresh();
    setFlash(r && r.message ? r.message : `Invoice sent to ${r && r.sent_to ? r.sent_to : recipient}.`);
  });
  // The company's today, not the viewer's: the paid day is a Connecticut day
  // on every report, wherever the bookkeeper is sitting.
  const companyDay = companyToday(settings);
  const zone = companyZone(settings);
  const openMarkPaid = () => { setPaidDay(companyDay); setPaidOpen(true); };
  // Today sends no day at all, so the server stamps the moment — exactly what
  // one press did before a day could be picked. An earlier day is named.
  const markPaid = () => run('paid', async () => {
    const r = await put(pathFor('markInvoicePaid', id), paidDayBody(paidDay, companyDay));
    setPaidOpen(false);
    await refresh();
    const said = paidDay && paidDay !== companyDay ? `Marked paid on ${fmtDate(paidDay)}.` : 'Marked paid.';
    const stripe = settledOutcome(r);
    if (stripe && stripe.tone === 'warn') setStripeNote(stripe);
    setFlash(stripe && stripe.tone === 'ok' ? `${said} ${stripe.text}` : said);
  });
  // Prefilled with what is owed, as classic did — `balance_due` is the
  // server's figure (invoices/list.js), so the screen subtracts nothing. The
  // server does the sum, says Partial or Paid, and refuses more than is owed.
  const openRecord = () => {
    setRecordAmount(doc && doc.balance_due ? String(doc.balance_due) : '');
    setRecordDay(companyDay);
    setRecordNote('');
    setRecordError(null);
    setRecordOpen(true);
  };
  const recordPayment = async () => {
    setBusy('record'); setRecordError(null); setFlash(null);
    try {
      const r = await post(pathFor('recordInvoicePayment', id), {
        amount: Number(recordAmount),
        ...paidDayBody(recordDay, companyDay),
        ...(recordNote.trim() ? { payment_note: recordNote.trim() } : {}),
      });
      setRecordOpen(false);
      await refresh();
      const stripe = settledOutcome(r);
      if (stripe && stripe.tone === 'warn') setStripeNote(stripe);
      setFlash(`Payment of ${fmtMoney(Number(recordAmount))} recorded.${r && r.status === 'paid' ? ' The invoice is paid in full.' : ''}${stripe && stripe.tone === 'ok' ? ` ${stripe.text}` : ''}`);
    } catch (e) {
      setRecordError(describeError(e, 'Could not record the payment.'));
    } finally {
      setBusy(null);
    }
  };
  const openRedate = () => { setRedateDay(paidDayOf(doc && doc.paid_at, zone) || companyDay); setRedateReason(''); setRedateOpen(true); };
  const redate = () => run('redate', async () => {
    await put(pathFor('changeInvoicePaidDate', id), { paid_on: redateDay, reason: redateReason.trim() });
    setRedateOpen(false);
    await refresh();
    setFlash(`Paid date changed to ${fmtDate(redateDay)}. The change is in the Activity log.`);
  });
  // The day it has now, and whether the picked one differs: the same day is
  // no change on the server (nothing written, nothing logged), so the button
  // is not offered for it rather than claiming a log entry that never happens.
  const currentPaidDay = paidDayOf(doc && doc.paid_at, zone);
  // A day before the invoice existed is legitimate — paid on site, written up
  // later — but it is also what a mistyped year looks like, so it is said.
  const createdDay = paidDayOf(doc && doc.created_at, zone);
  const beforeCreated = (day) => (createdDay && day && !paidDayProblem(day, companyDay) && day < createdDay
    ? `That is before this invoice was created (${fmtDate(createdDay)}). If it was paid before it was written up, that is fine — otherwise check the year.`
    : null);

  /**
   * Undo a mark-paid — the money did not actually arrive.
   *
   * The sheet says what will happen BEFORE it happens, because two parts of it
   * are not what a person would assume:
   *
   *   · the invoice goes back to SENT, or to PARTIAL if some money did land,
   *     so it is owed again and counts toward the outstanding total;
   *   · paid_at is CLEARED rather than kept. Keeping it would make a LATER,
   *     genuine payment inherit the old date (every mark-paid path COALESCEs
   *     it) and land that revenue in the wrong month. The record of the
   *     reversal — who, when, why — goes to the Activity log instead, which is
   *     where it can hold all three.
   */
  // On a PART-paid invoice the sheet opens at what is recorded: reversing one
  // is correcting a figure, and a blank box would mean "nothing arrived" and
  // erase a payment that did (second review of #739). Paid opens blank, as
  // it always has — there the question is what, if anything, landed.
  const openReverse = () => {
    setReverseCollected(doc && doc.status === 'partial' && Number(doc.amount_paid) > 0 ? String(doc.amount_paid) : '');
    setReverseOpen(true);
  };
  const markUnpaid = () => run('unpaid', async () => {
    await put(pathFor('markInvoiceUnpaid', id), {
      reason: reverseReason.trim(),
      // Omitted entirely when nothing landed, so the server reads it as 0
      // rather than as "a number I could not parse".
      ...(String(reverseCollected).trim() ? { amount_paid: Number(reverseCollected) } : {}),
    });
    setReverseOpen(false);
    setReverseReason('');
    setReverseCollected('');
    await refresh();
    setFlash('Payment reversed. The reason is on the invoice and in the Activity log.');
  });
  /**
   * Ask Stripe about THIS invoice.
   *
   * Three answers, and only one of them is good news:
   *   { updated: true, … }                       Stripe says paid; the row moved
   *   { updated: false, reason: 'no-stripe-record' }  nothing to compare against
   *   { updated: false, reason: … }              Stripe has it and it is not paid
   * The last two are reported as INFORMATION, never as a success, and never as
   * an error either — the request worked, the answer was just "no".
   */
  const syncPayment = () => run('sync', async () => {
    setSyncNote(null);
    const r = await post(pathFor('syncOnePayment', id));
    if (r && r.updated) {
      await refresh();
      setSyncNote({
        tone: r.amount_mismatch ? 'warn' : 'ok',
        text: r.message || `${r.invoice_number || 'This invoice'} was marked paid.`,
        mismatch: r.amount_mismatch ? mismatchSentence(r.amount_mismatch) : null,
      });
      return;
    }
    setSyncNote({
      tone: 'info',
      text: (r && r.message) || 'Stripe was asked and did not report a payment for this invoice.',
      mismatch: null,
    });
  });
  // The page the client opens from their email — Pay button or Paid — in a
  // new tab, so the office sees exactly what the client sees.
  // `noopener` makes window.open answer null, so there is nothing to check.
  const viewAsClient = () => { window.open(`/invoice/${doc.access_token}`, '_blank', 'noopener,noreferrer'); };
  const copyLink = () => run('copy', async () => {
    const url = `${window.location.origin}/invoice/${doc.access_token}`;
    const ok = await copyText(url);
    setFlash(ok ? `Copied ${url}` : `Could not copy — the link is ${url}`);
  });
  const downloadZip = () => run('zip', async () => { await downloadBlob(`/invoices/${id}/photos.zip`, `${doc.invoice_number || 'invoice'}-photos.zip`); });
  const downloadPdf = () => run('pdf', async () => { await downloadBlob(pathFor('invoicePdf', id), `Invoice-${String(doc.invoice_number || id).replace(/[^A-Za-z0-9._-]+/g, '-')}.pdf`); });
  // Cancel keeps the invoice on the record and closes every way to pay it:
  // the server voids its Stripe invoice and expires its Checkouts (routes/
  // invoices.js closeStripeFor; owner, 2026-09-28). Only the status is sent.
  const cancelInvoice = () => run('cancel', async () => {
    const r = await put(`/invoices/${id}`, { status: 'cancelled' });
    setCancelOpen(false);
    await qc.invalidateQueries({ queryKey: ['office', 'invoices'] });
    await refresh();
    setCancelNote(cancelOutcome(r));
  });
  const reinstate = () => run('reinstate', async () => {
    const to = reinstateTo(doc);
    await put(`/invoices/${id}`, { status: to });
    setReinstateOpen(false);
    setCancelNote(null);
    await qc.invalidateQueries({ queryKey: ['office', 'invoices'] });
    await refresh();
    setFlash(to === 'sent'
      ? 'Reinstated as Sent. Its client link takes payments again, and it counts in Money in & out.'
      : 'Reinstated as a draft.');
  });
  const remove = () => run('delete', async () => { await del(`/invoices/${id}`); setDeleteOpen(false); await qc.invalidateQueries({ queryKey: ['office', 'invoices'] }); navigate('/invoices', { replace: true }); });

  // "Uploading 12 of 30…" once there is more than one photo in flight; a single
  // one does not need a count. Same shape as JobPanels' uploadLabel.
  const attachLabel = photoProgress && photoProgress.total > 1
    ? `Uploading ${photoProgress.sent} of ${photoProgress.total}…`
    : 'Attaching…';

  const pill = doc ? invoicePill(doc, today) : null;
  const title = isNew ? 'New invoice' : (doc.invoice_number || `Invoice #${id}`);
  const meta = doc ? [
    doc.client_name,
    doc.due_date ? `Due ${fmtDate(doc.due_date)}` : null,
    doc.sent_at ? `Sent ${fmtDateTime(doc.sent_at)}` : null,
    doc.paid_at ? `Paid ${fmtDateTime(doc.paid_at)}` : null,
  ].filter(Boolean).join(' · ') : 'Fill in the client and the lines, then create it to get totals.';

  return (
    <>
      <PageHeader
        title={<>{title}{pill ? <> <Pill tone={pill.tone}>{pill.label}</Pill></> : null}</>}
        meta={meta}
        actions={doc ? (
          <>
            {/* Unpaid: the invoice with a Pay button. Paid: the same invoice
                marked Paid, no Pay button, nothing billed (server: payments.js
                `paidCopy`). Cancelled: nothing to send — the server refuses. */}
            {!cancelled && canSend ? <FwButton variant={paid ? 'ghost' : 'primary'} icon="mail" onClick={() => setSendOpen(true)} disabled={!!busy} data-action="send">{paid ? 'Send paid copy' : 'Send'}</FwButton> : null}
            {!paid && canMarkPaid ? <FwButton variant="go" icon="check" onClick={openMarkPaid} disabled={!!busy} data-action="mark-paid">Mark paid</FwButton> : null}
            {/* Not on a draft (the server refuses: 'partial' counts as issued)
                nor once nothing is owed. */}
            {!paid && !cancelled && canRecord && doc.status !== 'draft' && Number(doc.balance_due) > 0
              ? <FwButton variant="ghost" icon="dollar" onClick={openRecord} disabled={!!busy} data-action="record-payment">Record payment</FwButton> : null}
            {!paid && canSync ? <FwButton variant="ghost" icon="refresh" onClick={syncPayment} loading={busy === 'sync'} disabled={!!busy && busy !== 'sync'} data-action="sync-payment">Check Stripe</FwButton> : null}
            {mayUse('invoicePdf', role) ? <FwButton variant="ghost" icon="download" onClick={downloadPdf} loading={busy === 'pdf'} disabled={!!busy && busy !== 'pdf'} data-action="download-pdf">Download PDF</FwButton> : null}
            <FwButton variant="ghost" icon="document" onClick={() => setPrinting(true)} disabled={!!busy} data-action="print-view">Print</FwButton>
            {doc.access_token ? <FwButton variant="ghost" icon="eye" onClick={viewAsClient} data-action="view-as-client">View as client</FwButton> : null}
            {doc.access_token ? <FwButton variant="ghost" icon="link" onClick={copyLink} loading={busy === 'copy'}>Copy public link</FwButton> : null}
            {doc.photos && doc.photos.length ? <FwButton variant="ghost" icon="download" onClick={downloadZip} loading={busy === 'zip'}>Download photos</FwButton> : null}
            {paid && canRedate ? <FwButton variant="ghost" icon="calendar" onClick={openRedate} disabled={!!busy} data-action="change-paid-date">Change paid date</FwButton> : null}
            {/* Part-paid too (review of #739): a part payment recorded wrong
                is corrected the same way, to what actually arrived. */}
            {(paid || doc.status === 'partial') && canReverse ? <FwButton variant="stop" icon="arrowLeft" onClick={openReverse} disabled={!!busy} data-action="mark-unpaid">Reverse payment</FwButton> : null}
            {canUpdate && !paid && !cancelled ? <FwButton variant="ghost" icon="x" onClick={() => setCancelOpen(true)} disabled={!!busy} data-action="cancel-invoice">Cancel invoice</FwButton> : null}
            {canUpdate && cancelled ? <FwButton variant="ghost" icon="refresh" onClick={() => setReinstateOpen(true)} disabled={!!busy} data-action="reinstate">Reinstate</FwButton> : null}
            {/* Cancelled but still pointing at a Stripe invoice: the void was
                refused (the server clears the pointer once Stripe confirms),
                so it can still be paid there. Survives a reload, unlike a note. */}
            {canUpdate && cancelled && doc.stripe_invoice_id ? <FwButton variant="stop" icon="refresh" onClick={cancelInvoice} loading={busy === 'cancel'} disabled={!!busy && busy !== 'cancel'} data-action="void-in-stripe">Void in Stripe</FwButton> : null}
            {canDelete && !paid ? <FwButton variant="stop" icon="trash" onClick={() => setDeleteOpen(true)} disabled={!!busy} data-action="delete">Delete</FwButton> : null}
          </>
        ) : <FwButton variant="ghost" onClick={() => navigate('/invoices')}>Back to invoices</FwButton>}
      />
      {/* The amount, before anything else on the page. Measured at 320px
          before this landed: "Total $510.00" was at y=3133 of a 3891px page,
          four screenfuls below the fold, because the only place this invoice
          stated its own figure was TotalsPanel at the foot of the editor.
          Nothing is computed — see DocumentSummary. */}
      <DocumentSummary doc={doc} kind="invoice" today={today} />
      {flash ? <Notice tone="ok" onDismiss={() => setFlash(null)}>{flash}</Notice> : null}
      <ActionError error={error} onDismiss={() => setError(null)} />
      {/* A partial upload, named. Never green: some of what was picked is not
          on this invoice, and the sentence says how many and why. */}
      {photoNote ? <Notice tone="warn" onDismiss={() => setPhotoNote(null)} data-photo-note>{photoNote}</Notice> : null}
      {stripeNote ? <Notice tone={stripeNote.tone} onDismiss={() => setStripeNote(null)} data-stripe-note>{stripeNote.text}</Notice> : null}
      {cancelNote ? (
        <Notice tone={cancelNote.tone} onDismiss={() => setCancelNote(null)} data-cancel-note={cancelNote.tone}>
          {cancelNote.text}
          {/* One way to retry, never zero and never two: Void in Stripe is in
              the header while the invoice still points at a Stripe invoice;
              otherwise the note carries its own (review of #732). */}
          {cancelNote.failed ? (
            doc && doc.stripe_invoice_id
              ? <> Press Void in Stripe to try again, or close it in the Stripe dashboard.</>
              : <> {' '}<FwButton variant="ghost" icon="refresh" onClick={cancelInvoice} loading={busy === 'cancel'} disabled={!!busy && busy !== 'cancel'} data-action="retry-stripe-close">Try again</FwButton> or close it in the Stripe dashboard.</>
          ) : null}
        </Notice>
      ) : null}
      {syncNote ? (
        <Notice tone={syncNote.tone} onDismiss={() => setSyncNote(null)} data-sync-note={syncNote.tone}>
          {syncNote.text}
          {syncNote.mismatch ? (
            <> {' '}Stripe collected a different amount: {syncNote.mismatch} Those are the server&apos;s
              own figures, in cents, shown unconverted — nothing on the invoice was adjusted.</>
          ) : null}
        </Notice>
      ) : null}
      {paid ? (
        <Notice tone="info">
          This invoice is paid, so it is read-only.
          {canRedate ? ' If it was paid on a different day, use Change paid date.' : ''}
          {canReverse ? ' If the money did not actually arrive, use Reverse payment.' : ''}
        </Notice>
      ) : null}
      {doc && doc.total !== undefined && doc.amount_paid !== undefined && Number(doc.amount_paid) > 0 && !paid
        ? <Notice tone="info">{fmtMoney(doc.amount_paid)} of {fmtMoney(doc.total)} has been recorded as paid.</Notice> : null}
      {doc && doc.stripe_invoice_id ? <Notice tone="info" icon="dollar">Billed through Stripe{doc.payment_url ? <> — <a className="of-link" href={doc.payment_url} target="_blank" rel="noreferrer">payment page</a></> : null}.</Notice> : null}

      {/* The route back to the quote — docs/PARITY_2026-09-08.md §19.
          `from_estimate_id` is a real column on `invoices`, written by the
          conversion path, and this handler selects `i.*`, so the screen has had
          it all along and drew nothing. The question it answers is the one an
          operator gets on the phone: "your quote said X, why does the bill say
          Y?" — which needed two navigations and knowing the estimate's number.

          NAVIGATION rather than an <a href>: a full page load drops the
          react-query cache. The mirror of this on EstimateDetail ("Converted
          to invoice … Open it") does the same.

          `source_estimate_number` is preferred and is NOT on this answer — only
          the LIST joins it (routes/invoices.js:42) — so the id is the fallback
          and is what actually renders today. Reading a field the detail route
          does not send is how "estimate undefined" ships. */}
      {doc && doc.from_estimate_id ? (
        <Notice tone="info" icon="estimates">
          Converted from estimate {doc.source_estimate_number || `#${doc.from_estimate_id}`}.{' '}
          <FwButton
            variant="quiet"
            data-action="open-source-estimate"
            onClick={() => navigate(`/estimates/${doc.from_estimate_id}`)}
          >Open the estimate</FwButton>
        </Notice>
      ) : null}

      {/* Both of these lists feed a <select> inside DocumentEditor, and both are
          handed to it as `… .data || []`. Without these notices a refusal is
          indistinguishable from "there are none". The jobs one is not exotic:
          GET /api/invoices/available-jobs was checkRole('admin','manager')
          until 2026-09-09 while this screen enables the query on
          `canCreate`/`canUpdate`, so anyone holding invoice rights through
          extra_permissions was refused here every time. That gate reads the
          permission model now (server/routes/invoices.js, and
          server/utils/__tests__/invoicePickerPermissions.test.js holds the
          per-role answers). The notices stay: a 403 or a 500 still looks like
          an empty list, and silence would read as "there is no finished work".

          Both are gated on `canEdit`, because a read-only invoice disables the
          client <select> and does not render the job picker at all: warning
          about a control that is not there would be its own false claim. */}
      {canEdit && clients.isError ? (
        <PickerFailed
          what="the client list"
          error={clients.error}
          onRetry={() => clients.refetch()}
          consequence="The Client dropdown below is empty because the list was refused, not because the client is missing — check before typing the billing details in by hand, or you will create a second record for a client who is already on file."
        />
      ) : null}
      {canEdit && jobs.isError ? (
        <PickerFailed
          what="the jobs that can be billed"
          error={jobs.error}
          onRetry={() => jobs.refetch()}
          consequence="Jobs already linked to this invoice are still listed below; the picker just cannot offer new ones until this loads."
        />
      ) : null}

      <DocumentEditor
        kind="invoice"
        doc={doc}
        isNew={isNew}
        clients={clients.data || []}
        availableJobs={jobs.data || []}
        canEdit={canEdit}
        saving={saving}
        savedAt={savedAt}
        onSave={save}
      />

      {doc ? (
        <section className="of-card">
          <div className="of-section-head">
            <SectionLabel>Photos</SectionLabel>
            {/* The label IS the control: the input itself is visually hidden
                (.of-file input) so the hit target is the whole button, which is
                what a thumb finds. Mirrors JobPanels' Add photos. */}
            {canEdit && !isNew ? (
              <label className="of-file">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  disabled={photoBusy}
                  aria-label="Attach photos to this invoice"
                  data-action="add-photos"
                  onChange={(e) => { addPhotos(e.target.files); e.target.value = ''; }}
                />
                {/* Thirty photos over an office connection is several requests
                    and a real wait. A control that says only "Attaching…" for
                    that long is one somebody taps again or walks away from, so
                    it counts — the same sentence JobPanels shows. */}
                <span>{photoBusy ? attachLabel : 'Attach photos'}</span>
              </label>
            ) : null}
          </div>
          {doc.photos && doc.photos.length ? (
            <div className="of-gallery">
              {doc.photos.map((p) => (
                <figure className="of-gallery__item" key={p.id || p.media_id || p.file_path}>
                  <img src={p.file_path} alt={p.caption || 'Invoice photo'} loading="lazy" />
                  {p.caption ? <figcaption>{p.caption}</figcaption> : null}
                  {canEdit ? (
                    <FwButton
                      variant="ghost"
                      icon="trash"
                      disabled={photoBusy}
                      data-action="remove-photo"
                      onClick={() => setDropPhoto(p)}
                    >Remove</FwButton>
                  ) : null}
                </figure>
              ))}
            </div>
          ) : (
            <EmptyState icon="image" title="No photos on this invoice">
              {canEdit && !isNew
                ? 'Photos come across from linked jobs. Attach photos to add before-and-after shots the client should see.'
                : 'Photos come across from linked jobs.'}
            </EmptyState>
          )}
        </section>
      ) : null}

      {/* The audit line — docs/PARITY_2026-09-08.md §14. `created_by_name` has
          been on this route since it was written and nothing drew it, so "who
          raised that invoice" needed the Activity log. An invoice carries the
          estimate workflow's approved_at / accepted_at columns as NULL, and
          ProvenanceCard keys off the VALUES, so it never claims an approval a
          bill never had. */}
      <ProvenanceCard doc={doc} />

      {/* Removing a photo is a delete on the server — the gallery is REPLACED
          by whatever this screen sends — so it is confirmed rather than being
          one mis-tap away. */}
      <ConfirmSheet
        open={!!dropPhoto}
        title="Remove this photo?"
        onClose={() => setDropPhoto(null)}
        onConfirm={removePhoto}
        confirmLabel="Remove"
        tone="stop"
        busy={photoBusy}
      >
        <p>It comes off this invoice, including the copy the client can open. Photos on the linked job are not touched.</p>
      </ConfirmSheet>

      {/* Marking it paid asks WHICH DAY, defaulting to the company's today.
          The owner, 2026-09-27: an invoice marked paid in January for money
          that arrived in December must not land on next year's report. */}
      <ConfirmSheet
        open={paidOpen}
        title="Mark this invoice paid?"
        onClose={() => setPaidOpen(false)}
        onConfirm={markPaid}
        confirmLabel="Mark paid"
        tone="go"
        busy={busy === 'paid'}
        confirmDisabled={!!paidDayProblem(paidDay, companyDay)}
      >
        <PaidDayField id="of-paid-on" value={paidDay} onChange={setPaidDay} today={companyDay} />
        {beforeCreated(paidDay) ? <Notice tone="warn" data-paid-before-created>{beforeCreated(paidDay)}</Notice> : null}
        <p className="of-muted">
          The full {fmtMoney(doc && doc.total)} is recorded as paid on {paidDay && !paidDayProblem(paidDay, companyDay) ? fmtDate(paidDay) : 'the day you pick'}.
        </p>
      </ConfirmSheet>

      <ConfirmSheet
        open={recordOpen}
        title="Record a payment"
        onClose={() => setRecordOpen(false)}
        onConfirm={recordPayment}
        confirmLabel="Record payment"
        tone="go"
        busy={busy === 'record'}
        confirmDisabled={!(Number(recordAmount) > 0) || !!paidDayProblem(recordDay, companyDay)}
      >
        <p className="of-muted" data-record-owed>
          Still owed: <strong className="fw-num">{fmtMoney(doc && doc.balance_due)}</strong> of {fmtMoney(doc && doc.total)}.
        </p>
        <FwField label="Amount received" htmlFor="of-record-amount" required hint="Paying the full balance marks the invoice paid.">
          <FwInput
            id="of-record-amount"
            type="number"
            inputMode="decimal"
            min="0.01"
            step="0.01"
            value={recordAmount}
            onChange={(e) => setRecordAmount(e.target.value)}
            data-field="record-amount"
          />
        </FwField>
        {/* A part payment has no date column of its own (Money in & out counts
            it as received, date not recorded — utils/moneySummary.js), so the
            day goes to the Activity log; the payment that clears the balance
            sets the invoice's paid date, which is what the reports read. */}
        <PaidDayField
          id="of-record-on"
          label="Received on"
          value={recordDay}
          onChange={setRecordDay}
          today={companyDay}
          hint="Kept with this payment in the Activity log. If it pays the invoice off, it is also the paid date the reports count it on."
        />
        <FwField label="Note (optional)" htmlFor="of-record-note" hint="For the office: kept in the Activity log, never shown to the client.">
          <FwInput id="of-record-note" value={recordNote} onChange={(e) => setRecordNote(e.target.value)} placeholder="Cheque no. 1182" />
        </FwField>
        {recordError ? <Notice tone="stop" data-record-error>{recordError}</Notice> : null}
      </ConfirmSheet>

      {/* Changing the day a paid invoice was paid. Nothing else moves — the
          amount, the status and the lines stay — and the old day, the new one
          and the reason go to the Activity log. */}
      <ConfirmSheet
        open={redateOpen}
        title="Change the paid date?"
        onClose={() => setRedateOpen(false)}
        onConfirm={redate}
        confirmLabel="Change date"
        busy={busy === 'redate'}
        confirmDisabled={!!paidDayProblem(redateDay, companyDay) || redateDay === currentPaidDay}
      >
        <PaidDayField id="of-redate-on" value={redateDay} onChange={setRedateDay} today={companyDay} />
        {beforeCreated(redateDay) ? <Notice tone="warn" data-paid-before-created>{beforeCreated(redateDay)}</Notice> : null}
        {doc && doc.stripe_invoice_id ? (
          <Notice tone="info" data-paid-through-stripe>
            This invoice was billed through Stripe. If Stripe took the payment, the day it settled is the right one — change it only to correct a mistake.
          </Notice>
        ) : null}
        <FwField
          label="Why (optional)"
          htmlFor="of-redate-reason"
          hint="Recorded in the Activity log with your name, the old day and the new one."
        >
          <FwInput
            id="of-redate-reason"
            value={redateReason}
            onChange={(e) => setRedateReason(e.target.value)}
            placeholder="Cheque arrived Dec 30, marked paid late"
          />
        </FwField>
        <p className="of-muted">
          It is recorded as paid on {currentPaidDay ? fmtDate(currentPaidDay) : 'no day yet'}. Only the date changes — the amount and status stay as they are.
          {redateDay === currentPaidDay ? ' Pick a different day to change it.' : ''}
        </p>
      </ConfirmSheet>

      {/* Reversing a payment. The sheet states the outcome BEFORE it happens,
          computed by the same rule the server applies (reversalOutcome), so a
          confirm never guesses at what it is about to do. */}
      <ConfirmSheet
        open={reverseOpen}
        title="Reverse this payment?"
        onClose={() => setReverseOpen(false)}
        onConfirm={markUnpaid}
        confirmLabel="Reverse payment"
        tone="stop"
        busy={busy === 'unpaid'}
      >
        <FwField
          label="Why is it being reversed"
          htmlFor="of-reverse-reason"
          hint="Recorded on the invoice and in the Activity log, with your name."
          required
        >
          <FwInput
            id="of-reverse-reason"
            value={reverseReason}
            onChange={(e) => setReverseReason(e.target.value)}
            placeholder="Cheque bounced"
          />
        </FwField>
        <FwField
          label="How much DID arrive"
          htmlFor="of-reverse-collected"
          hint="Leave blank if none of it did."
        >
          <FwInput
            id="of-reverse-collected"
            type="number"
            min="0"
            step="0.01"
            value={reverseCollected}
            onChange={(e) => setReverseCollected(e.target.value)}
            placeholder="0.00"
          />
        </FwField>
        {(() => {
          const out = reversalOutcome(doc, reverseCollected);
          if (out.problem) return <Notice tone="stop" data-reverse-problem>{out.problem}</Notice>;
          return (
            <Notice tone="warn" data-reverse-outcome={out.status}>
              This invoice goes back to <strong>{out.status === 'partial' ? 'Partial' : 'Sent'}</strong>
              {out.status === 'partial'
                ? <> with {fmtMoney(out.collected)} recorded against it,</>
                : <> with nothing recorded against it,</>}
              {' '}so {out.status === 'partial' ? 'the balance' : 'the whole amount'} is owed again and
              counts toward the outstanding total.
              {doc && doc.status === 'paid'
                ? <>{' '}The paid date is cleared, so if it is genuinely paid later that payment records its
                  own date — the reversal itself is kept in the Activity log.</>
                : <>{' '}The reversal is kept in the Activity log.</>}
            </Notice>
          );
        })()}
      </ConfirmSheet>

      {/* The SEND here is disabled only when this screen can state the
          server's own refusal. `to.refusal` is non-null exactly when
          sendRecipient could see everywhere an address might be — see its
          header for the inactive-client case, where the screen holds no
          client row, says so, and leaves the button live rather than
          inventing a refusal the server would not make. */}
      <ConfirmSheet
        open={sendOpen}
        title={paid ? 'Send the paid invoice?' : 'Send this invoice?'}
        onClose={() => setSendOpen(false)}
        onConfirm={send}
        confirmLabel={paid ? 'Send paid copy' : 'Send'}
        busy={busy === 'send'}
        confirmDisabled={!!to.refusal}
      >
        {recipient ? (
          <p>
            It will go to <strong data-recipient={recipient}>{recipient}</strong>{to.source === 'client' && to.email !== (doc && doc.client_email) ? ' (the client record, which is newer than the address on the invoice)' : ''}.
            {paid
              ? ' The client gets the invoice marked Paid, with the PDF attached and no Pay button. Nothing is charged.'
              : ' The client gets the itemised invoice, the PDF attached, and a Pay button for the balance.'}
          </p>
        ) : to.refusal ? (
          <Notice tone="stop" data-send-blocked="no-recipient">{to.refusal} Sending would be refused.</Notice>
        ) : (
          <Notice tone="warn" data-send-blocked="unknown">
            No address is on this invoice, and this screen could not read the client&apos;s record — the
            roster it holds covers active clients only. The server looks the client up either way, so
            Send is still offered; if there is genuinely no address on file it will refuse and say so.
          </Notice>
        )}
      </ConfirmSheet>
      {/* "Yes, cancel it", not "Cancel invoice": the sheet's own dismiss is
          labelled Cancel, and two "cancel"s side by side on a phone is a guess
          (review of #732). */}
      <ConfirmSheet open={cancelOpen} title="Cancel this invoice?" onClose={() => setCancelOpen(false)} onConfirm={cancelInvoice} confirmLabel="Yes, cancel it" tone="stop" busy={busy === 'cancel'}>
        <p>
          {doc ? doc.invoice_number : 'This invoice'} stays on the record as Cancelled, and the client can no longer pay it:
          its Pay link stops taking payments and its Stripe invoice is voided. Its jobs stay billed on it; delete the invoice instead to bill that work again.
        </p>
        {/* A cancelled invoice leaves Money in & out, part-payments with it
            (utils/moneySummary.js). Said here with the stored figure, so money
            that is in the bank never drops out of the books unannounced. */}
        {/* Not on a draft: Money in & out never counted one (moneySummary.js). */}
        {doc && doc.status !== 'draft' && Number(doc.amount_paid) > 0 ? (
          <p data-cancel-warning="paid">
            {fmtMoney(doc.amount_paid)} is recorded as paid on it. Once it is cancelled, that money no longer counts in Money in &amp; out.
          </p>
        ) : null}
        {error ? <p className="of-muted">{describeError(error)}</p> : null}
      </ConfirmSheet>
      <ConfirmSheet open={reinstateOpen} title="Reinstate this invoice?" onClose={() => setReinstateOpen(false)} onConfirm={reinstate} confirmLabel="Reinstate" busy={busy === 'reinstate'}>
        <p data-reinstate-to={doc ? reinstateTo(doc) : undefined}>
          {doc && reinstateTo(doc) === 'sent'
            ? <>{doc.invoice_number || 'This invoice'} goes back to Sent. The client can pay it again from its link, and it counts in Money in &amp; out again. If its Stripe invoice was voided, that one stays voided: Send emails a new one.</>
            : <>{doc ? doc.invoice_number : 'This invoice'} goes back to Draft, as it was before it was cancelled. Send it when it should be billed.</>}
        </p>
        {error ? <p className="of-muted">{describeError(error)}</p> : null}
      </ConfirmSheet>
      <ConfirmSheet open={deleteOpen} title="Delete this invoice?" onClose={() => setDeleteOpen(false)} onConfirm={remove} confirmLabel="Delete" tone="stop" busy={busy === 'delete'}>
        <p>{doc ? doc.invoice_number : 'This invoice'} and its lines will be removed. Linked jobs become billable again. This cannot be undone.</p>
        {error ? <p className="of-muted">{describeError(error)}</p> : null}
      </ConfirmSheet>
    </>
  );
}
